import cn from 'clsx';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useContext, useRef } from 'react';
import Typography from '@material-ui/core/Typography';
import Lock from '@material-ui/icons/Lock';
import { CalendarContext } from '@modules/planner/calendar/calendar-context';
import { BulkOperationsContext } from '@modules/planner/calendar/controls/bulk-operations/bulk-operations-context';
import { useBulkOperationsTaskStyles } from '@modules/planner/calendar/controls/bulk-operations/bulk-operations-styles';
import { getTaskBoundaries } from '@modules/planner/calendar/utils';
import { getTaskStart } from '@modules/planner/calendar/utils/get-task-start';
import { getTaskWidth } from '@modules/planner/calendar/utils/get-task-width';
import { useIsKeyPressed } from '@modules/planner/dnd/utils/is-key-pressed';
import { getWorkOrderBusinessId } from '@modules/planner/work-order-id-utils';
import {
  WorkOrderMenu,
  WorkOrderMenuHandle,
} from '@modules/planner/work-order-menu/work-order-menu';
import { getPreviousWorkOrder } from '@composite/calendar/utils';
import { TaskTooltip } from '@composite/work-order/task-tooltip/task-tooltip';
import { WorkOrder } from '@models';
import { Draggable } from '../../../../../dnd/draggable';
import { useStyles } from './task-styles';
import { TravelTime } from './travel-time';

dayjs.extend(duration);

const draggableWoStates = ['NAP', 'POZ'];

const filterWorkOrderFromPreviousDay = (
  workOrder: WorkOrder | undefined,
  selectedWorkOrder: WorkOrder | undefined
) => {
  if (!workOrder || !selectedWorkOrder) {
    return undefined;
  }
  if (
    dayjs(workOrder.endTime).day() === dayjs(selectedWorkOrder.startTime).day()
  ) {
    return workOrder;
  }
  return undefined;
};

const getDisplayedWorkOrderId = (workOrder: WorkOrder) =>
  getWorkOrderBusinessId(workOrder) ?? workOrder.workOrderId ?? '';

export function Task({
  workOrder,
  overlayCount,
  position,
}: {
  workOrder: WorkOrder;
  overlayCount: number;
  position: number;
}) {
  const classes = useStyles();
  const bulkClasses = useBulkOperationsTaskStyles();
  const { settings, currentDate, dataSource } = useContext(CalendarContext);

  const workOrderMenuRef = useRef<WorkOrderMenuHandle>(null);
  const {
    bulkEditModeOn,
    addToSelection,
    selected,
    removeFromSelection,
    getSelectedWorkOrders,
  } = useContext(BulkOperationsContext);

  const { isKeyPressed } = useIsKeyPressed('Control');

  const { workOrderStart, workOrderEnd } = getTaskBoundaries(
    currentDate,
    settings,
    workOrder
  );

  const taskSize = Math.max(
    getTaskWidth(currentDate, workOrderStart, workOrderEnd, settings),
    10
  );

  const taskStart = getTaskStart(
    currentDate,
    workOrderStart,
    workOrderEnd,
    settings
  );

  const unit = dataSource.yAxis.data.find(
    (unit) =>
      unit.id === workOrder.mobileUnit?.id || unit.id === workOrder.user?.id
  );
  const previousWorkOrder = filterWorkOrderFromPreviousDay(
    getPreviousWorkOrder(
      unit?.workOrders ?? [],
      dayjs(workOrder.startTime).unix()
    ),
    workOrder
  );

  const taskHeightBase = settings.rowHeight * 0.9;
  const taskIsSelectedInBulkMode =
    bulkEditModeOn && selected.find((s) => s.id === workOrder.id);

  const selectedWos = getSelectedWorkOrders(unit?.id);

  const getIsDraggable = () => {
    if (!isKeyPressed) return false;

    if (
      workOrder.locked ||
      !draggableWoStates.includes(workOrder.currentState?.code ?? '')
    )
      return false;

    if (bulkEditModeOn) {
      // bulk edit on
      if (selectedWos.length === 0) return false;
      if (selectedWos.length !== selected.length) return false;
      if (!selectedWos.some((wo) => wo.id === workOrder?.id)) return false;
    }

    return true;
  };

  const isDraggable = getIsDraggable();

  return (
    <>
      <TaskTooltip workOrder={workOrder}>
        <div
          className={cn(classes.root, {
            [classes.isMultiDay]: workOrder.isMultiDay,
            [classes.isLocked]: workOrder.locked,
            [bulkClasses.on]: bulkEditModeOn,
            [bulkClasses.isSelected]: taskIsSelectedInBulkMode,
          })}
          onClick={() => {
            if (bulkEditModeOn && workOrder.id) {
              if (selected.find((s) => s.id === workOrder.id)) {
                removeFromSelection(workOrder.id);
              } else {
                addToSelection({
                  id: workOrder.id,
                  isMultiTask: false,
                  workOrderId: getWorkOrderBusinessId(workOrder),
                  unitId: workOrder.mobileUnit?.id ?? workOrder.user?.id ?? '',
                });
              }
            }
          }}
          style={{
            cursor: isKeyPressed
              ? isDraggable
                ? 'grab'
                : 'not-allowed'
              : undefined,
            opacity: isKeyPressed ? (isDraggable ? 1 : 0.3) : 1,
            backgroundColor: taskIsSelectedInBulkMode
              ? undefined
              : workOrder.currentState?.color,
            left: taskStart,
            width: Math.max(taskSize, 10),
            minWidth: 10,
            height: taskHeightBase / overlayCount,
            top:
              settings.rowHeight * 0.05 +
              (taskHeightBase * (position - 1)) / overlayCount,
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            workOrderMenuRef.current?.open(e);
          }}
        >
          {!!workOrder.travelPrevious && (
            <TravelTime orientation="left" seconds={workOrder.travelPrevious} />
          )}
          <Draggable
            id={bulkEditModeOn ? `bulk-plannig-${unit?.id}` : workOrder.id}
            data={bulkEditModeOn ? selectedWos : [workOrder]}
            disabled={!isDraggable}
            className={classes.content}
          >
            <Typography
              variant="body2"
              className={classes.title}
              style={{
                color: workOrder.currentState?.color,
              }}
            >
              # {getDisplayedWorkOrderId(workOrder)}{' '}
              {workOrder.isMultiDay && '(viacdenný)'}
            </Typography>
            {workOrder.locked && (
              <div className={classes.lockIcon}>
                <Lock fontSize="small" />
              </div>
            )}
          </Draggable>
          {!!workOrder.travelNext && (
            <TravelTime orientation="right" seconds={workOrder.travelNext} />
          )}
        </div>
      </TaskTooltip>
      <WorkOrderMenu
        previousWorkOrder={previousWorkOrder}
        workOrder={workOrder}
        ref={workOrderMenuRef}
        options={{
          allDayRouteOption: true,
          showInWorkOrderTableOption: true,
        }}
      />
    </>
  );
}
